import { Box, FormControlLabel, IconButton, Skeleton, Typography } from '@mui/material';
import { useCallback, useEffect, useReducer, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ReactComponent as DeleteIcon } from '../../../../assets/delete-red.svg';
import renameIcon from '../../../../assets/pen.svg';
import CustomAlert from '../../../../components/shared/CustomAlert/CustomAlert';
import CustomButton from '../../../../components/shared/CustomButton/CustomButton';
import CustomDeleteModal from '../../../../components/shared/CustomDeleteModal/CustomDeleteModal';
import CustomInputModal from '../../../../components/shared/CustomInputModal/CustomInputModal';
import { IOSSwitch } from '../../../../components/shared/IOSSwitch/IOSSwitch';
import { initialAlertState, useAlertStatus } from '../../../../customHook/useAlertStatus';
import { Role, TableType, UpdateRolePayload } from '../../../../model/rolesAndPerms.model';
import { modalReducer } from '../../../../reducers/modalReducer';
import RoutePaths from '../../../../routes/RoutePaths';
import { AdminService } from '../../../../service/admin.service';
import zeplinColor from '../../../../theme/Color';
import {
  mapRolePermissionsToTables,
  mapTablesToRolePermissions,
  tables as defaultTables,
} from '../../../../utilities/rolesAndPermsUtils';
import { PermissionTable } from './PermissionTable.tsx';

export const SelectedRoleAndPermission = () => {
  const navigate = useNavigate();
  const roleId = useLocation().pathname.split('/').at(-1);
  const [roleDetails, setRoleDetails] = useState<Role>();
  const [loading, setLoading] = useState(false);
  const [modalState, modalDispatch] = useReducer(modalReducer, {
    rename: false,
    delete: false,
  });

  const [tables, setTables] = useState<TableType[]>(defaultTables);

  const { alertState, startOperation, completeOperation, setAlertState, getAlertContent } =
    useAlertStatus();

  const buildUpdatePayload = useCallback(
    (overrides: Partial<UpdateRolePayload> = {}): UpdateRolePayload | null => {
      if (!roleDetails) return null;
      return {
        roleName: roleDetails.roleName,
        salesforceRoleId: roleDetails.salesforceRoleId || '',
        description: roleDetails.description || '',
        permissions: roleDetails.isSuperAdmin ? [] : mapTablesToRolePermissions(tables),
        isSuperAdmin: Boolean(roleDetails.isSuperAdmin),
        admin: Boolean(roleDetails.admin),
        ...overrides,
      };
    },
    [roleDetails, tables],
  );

  const handleSave = async () => {
    try {
      startOperation('upload', 'Saving permission changes...');
      const payload = buildUpdatePayload();
      if (!roleId || !payload) return;
      await AdminService.updateRole(roleId, payload);
      completeOperation('upload', 'Permissions updated successfully');
    } catch (error) {
      completeOperation('upload', 'Failed to save permissions', true);
    }
  };

  const fetchRoleDetails = useCallback(async () => {
    try {
      setLoading(true);
      if (!roleId) return;
      const data = await AdminService.getRoleDetails(roleId);
      setRoleDetails(data.role);
      setTables(mapRolePermissionsToTables(data.role?.permissions || []));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  const onRenameClick = async (newName: string) => {
    try {
      startOperation('rename', 'Updating name...');
      modalDispatch({ type: 'CLOSE', payload: 'rename' });
      const payload = buildUpdatePayload({ roleName: newName });
      if (roleId && payload) await AdminService.updateRole(roleId, payload);
      await fetchRoleDetails();
      completeOperation('rename', 'Updated name successfully');
    } catch (error: any) {
      completeOperation('rename', `Update Failed: ${error.message}`, true);
    }
  };

  const onDeleteClick = async () => {
    try {
      startOperation('delete', 'Deleting role...');
      modalDispatch({ type: 'CLOSE', payload: 'delete' });
      await AdminService.deleteRole(roleId!);

      navigate(`${RoutePaths.Home}/admin`);
      completeOperation('delete', 'Role deleted successfully');
    } catch (error) {
      completeOperation('delete', `Deletion Failed: ${error}`, true);
    }
  };

  useEffect(() => {
    fetchRoleDetails();
  }, [fetchRoleDetails, roleId]);

  if (loading || !roleDetails) {
    return (
      <Box
        sx={{
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'scroll',
          height: 'calc(100vh - 80px)',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Skeleton
              variant='text'
              sx={{
                width: '20%',
                height: '48px',
              }}
            />
            <Skeleton variant='circular' sx={{ width: '28px', height: '28px' }} />
          </Box>
          <Skeleton
            variant='text'
            sx={{
              width: '40%',
            }}
          />
        </Box>

        <Skeleton
          variant='rounded'
          sx={{
            marginTop: '24px',
            height: '50%',
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'scroll',
        height: 'calc(100vh - 80px)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <Typography variant='heading1'>{roleDetails.roleName}</Typography>
            <IconButton
              sx={{ width: '28px', height: '28px' }}
              onClick={(e) => {
                e.stopPropagation();
                modalDispatch({ type: 'OPEN', payload: 'rename' });
              }}
            >
              <img src={renameIcon} alt='rename icon' width={'24px'} height={'24px'} />
            </IconButton>
          </Box>
          <Typography variant='body3'>
            Customize access levels for this role by enabling or disabling permissions for each
            module.
          </Typography>
        </Box>
        <Box>
          <CustomButton
            disabled={false}
            variant='text'
            text='Delete Role'
            onClick={() => {
              modalDispatch({ type: 'OPEN', payload: 'delete' });
            }}
            iconPosition='end'
            icon={<DeleteIcon />}
            style={{
              borderRadius: '14px',
              height: '48px',
              padding: '0px 24px',
              color: zeplinColor.Error,
            }}
            buttonVariant='buttonMedium'
          />
          <CustomButton
            disabled={false}
            variant='contained'
            text='Save'
            onClick={handleSave}
            style={{
              borderRadius: '14px',
              height: '48px',
              padding: '0px 24px',
            }}
            buttonVariant='buttonMedium'
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: '24px', mt: '16px', mb: '8px' }}>
        <FormControlLabel
          control={
            <IOSSwitch
              checked={Boolean(roleDetails.isSuperAdmin)}
              onChange={(e) =>
                setRoleDetails((prev) =>
                  prev ? { ...prev, isSuperAdmin: e.target.checked } : prev,
                )
              }
            />
          }
          label={
            <Typography variant='body2' color={zeplinColor.BasicBlack} sx={{ ml: '8px' }}>
              Super Admin
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <IOSSwitch
              checked={Boolean(roleDetails.admin)}
              onChange={(e) =>
                setRoleDetails((prev) => (prev ? { ...prev, admin: e.target.checked } : prev))
              }
            />
          }
          label={
            <Typography variant='body2' color={zeplinColor.BasicBlack} sx={{ ml: '8px' }}>
              Admin
            </Typography>
          }
        />
      </Box>

      {tables.map((table) => (
        <PermissionTable
          key={table.id}
          table={table}
          onChange={(updated) =>
            setTables((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))
          }
        />
      ))}

      <CustomInputModal
        modalType={'rename'}
        title={'Rename Role'}
        label='Role name'
        isModalOpen={modalState.rename}
        modalDispatch={modalDispatch}
        handleAction={onRenameClick}
        inputValue={roleDetails.roleName}
      />

      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />

      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={onDeleteClick}
        title='Delete Role'
        description={`You’re about to delete the role “${roleDetails.roleName}” This action will remove all permissions associated with it. Are you sure you want to delete?`}
      />
    </Box>
  );
};
